"use client"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
    Coins,
    Globe,
    Layers,
    Vote,
    Shield,
    TrendingUp,
    LucideIcon,
    Copy,
    Check,
    ExternalLink,
    FileCode,
    DollarSign,
    CreditCard,
    Users,
    Wallet,
    CheckCircle2,
    ArrowRight,
} from "lucide-react"

const utilities: { icon: LucideIcon; title: string; description: string }[] = [
    {
        icon: Coins,
        title: "Platform Fees",
        description: "Pay reduced fees when using TKS for token creation, presales, and locking",
    },
    {
        icon: TrendingUp,
        title: "Staking Rewards",
        description: "Stake TKS to earn passive income with duration-based APY",
    },
    {
        icon: Vote,
        title: "Governance",
        description: "Vote on platform proposals and shape the future of TokenKickstarter",
    },
    {
        icon: Shield,
        title: "Priority Access",
        description: "Get early access to new presales and exclusive platform features",
    },
]

const tokenInfo = {
    name: "TokenKickstarter",
    symbol: "TKS",
    decimals: 18,
    totalSupply: "1B / network",
    networks: ["BSC", "Ethereum", "Base (Coming Soon)", "Polygon (Coming Soon)"],
}

const presaleInfo = {
    currentPrice: "$0.01",
    rate: "100 TKS per $1",
    presale: "Live",
    referralBonus: "5%",
    minContribution: "No minimum",
    maxContribution: "No maximum",
}

const acceptedCurrencies = [
    { name: "BNB", type: "Native" },
    { name: "ETH", type: "Native" },
    { name: "MATIC", type: "Native" },
    { name: "SOL", type: "Native" },
    { name: "TRX", type: "Native" },
    { name: "TON", type: "Native" },
    { name: "USDT", type: "Stablecoin" },
    { name: "USDC", type: "Stablecoin" },
    { name: "DAI", type: "Stablecoin" },
]

const participateSteps: { icon: LucideIcon; title: string; description: string }[] = [
    {
        icon: Wallet,
        title: "Connect Your Wallet",
        description: "Connect MetaMask, WalletConnect, or any Web3 wallet",
    },
    {
        icon: CreditCard,
        title: "Choose Payment Method",
        description: "Select your preferred currency (ETH, BNB, USDT, etc.)",
    },
    {
        icon: DollarSign,
        title: "Enter Amount",
        description: "Specify how much you want to contribute",
    },
    {
        icon: CheckCircle2,
        title: "Confirm & Receive",
        description: "Confirm transaction and your TKS is recorded for claim",
    },
]

// Smart contract addresses from env vars
const contracts = {
    testnet: {
        token: [
            {
                network: "BSC Testnet",
                address: process.env.NEXT_PUBLIC_TKS_TOKEN_ADDRESS || "0x8664E4345E2181032F1dDfbCCE210456C0eD8401",
                explorer: "https://testnet.bscscan.com",
            },
            {
                network: "Sepolia",
                address: process.env.NEXT_PUBLIC_TKS_TOKEN_ADDRESS || "0x8664E4345E2181032F1dDfbCCE210456C0eD8401",
                explorer: "https://sepolia.etherscan.io",
            },
        ],
        vault: [
            {
                network: "BSC Testnet",
                address: process.env.NEXT_PUBLIC_TKS_VAULT_BSC_TESTNET || "0xacCB3Fac4724Ed22181Ba484E6bd0F0965924103",
                explorer: "https://testnet.bscscan.com",
            },
            {
                network: "Sepolia",
                address: process.env.NEXT_PUBLIC_TKS_VAULT_SEPOLIA || "0xacCB3Fac4724Ed22181Ba484E6bd0F0965924103",
                explorer: "https://sepolia.etherscan.io",
            },
        ],
    },
    mainnet: {
        token: [
            {
                network: "BSC",
                address: process.env.NEXT_PUBLIC_TKS_TOKEN_BSC || "0x420dbEf020711832d0a210be37270EFA8a1c00E4",
                explorer: "https://bscscan.com",
            },
            {
                network: "Ethereum",
                address: process.env.NEXT_PUBLIC_TKS_TOKEN_ETH || "0x420dbEf020711832d0a210be37270EFA8a1c00E4",
                explorer: "https://etherscan.io",
            },
        ],
        vault: [
            {
                network: "BSC",
                address: process.env.NEXT_PUBLIC_TKS_VAULT_BSC || "0xa79289DB22eB16013d8044a64050691f4F3261C1",
                explorer: "https://bscscan.com",
            },
            {
                network: "Ethereum",
                address: process.env.NEXT_PUBLIC_TKS_VAULT_ETH || "0xa79289DB22eB16013d8044a64050691f4F3261C1",
                explorer: "https://etherscan.io",
            },
        ],
    },
}

function AddressRow({ network, address, explorer }: { network: string; address: string; explorer: string }) {
    const [copied, setCopied] = useState(false)

    const handleCopy = () => {
        navigator.clipboard.writeText(address)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    const shortAddress = `${address.slice(0, 6)}...${address.slice(-4)}`

    return (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/20 group hover:bg-muted/40 transition-all">
            <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap min-w-[80px]">{network}</span>
                <code className="text-sm font-mono truncate hidden sm:block">{address}</code>
                <code className="text-sm font-mono sm:hidden">{shortAddress}</code>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                <button
                    onClick={handleCopy}
                    className="p-1.5 rounded-md hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-all"
                    title="Copy address"
                >
                    {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
                <a
                    href={`${explorer}/address/${address}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-md hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-all"
                    title="View on explorer"
                >
                    <ExternalLink className="h-3.5 w-3.5" />
                </a>
            </div>
        </div>
    )
}

export function TokenDetailsSection() {
    return (
        <section className="py-20 md:py-28" id="token-details">
            <div className="container">
                <div className="mb-16 text-center">
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
                        TKS Token
                    </div>
                    <h2 className="mb-5 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
                        Token{" "}
                        <span className="bg-gradient-to-r from-primary via-primary to-primary/70 bg-clip-text text-transparent">
                            Details
                        </span>
                    </h2>
                    <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
                        TKS is the native utility token powering the TokenKickstarter ecosystem
                    </p>
                </div>

                {/* Row 1: Token Information + Current Pricing */}
                <div className="grid gap-8 lg:grid-cols-2">
                    {/* Token Info Card */}
                    <div className="glass-card p-8">
                        <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                            <Coins className="h-5 w-5 text-primary" />
                            Token Information
                        </h3>

                        <div className="space-y-4">
                            <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
                                <span className="text-muted-foreground">Token Name</span>
                                <span className="font-semibold">{tokenInfo.name}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
                                <span className="text-muted-foreground">Symbol</span>
                                <span className="font-mono font-bold text-primary">${tokenInfo.symbol}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
                                <span className="text-muted-foreground">Official Logo</span>
                                <div className="h-12 w-12 rounded-full overflow-hidden shadow-md shadow-primary/10">
                                    <Image src="/token_logo.png" alt="TKS Token" width={48} height={48} className="w-full h-full object-cover" unoptimized />
                                </div>
                            </div>
                            <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
                                <span className="text-muted-foreground">Decimals</span>
                                <span className="font-semibold">{tokenInfo.decimals}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
                                <span className="text-muted-foreground">Total Supply</span>
                                <span className="font-semibold">{tokenInfo.totalSupply} TKS</span>
                            </div>
                            <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
                                <span className="text-muted-foreground">Rate</span>
                                <span className="font-semibold">{presaleInfo.rate}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
                                <span className="text-muted-foreground">Presale</span>
                                <span className="font-semibold text-[#7ed957]">{presaleInfo.presale}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
                                <span className="text-muted-foreground">Referral Bonus</span>
                                <span className="font-semibold">{presaleInfo.referralBonus}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
                                <span className="text-muted-foreground">Min Contribution</span>
                                <span className="font-semibold">{presaleInfo.minContribution}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
                                <span className="text-muted-foreground">Max Contribution</span>
                                <span className="font-semibold">{presaleInfo.maxContribution}</span>
                            </div>
                            <div className="p-4 rounded-xl bg-muted/30">
                                <div className="flex justify-between items-center mb-3">
                                    <span className="text-muted-foreground flex items-center gap-2">
                                        <Globe className="h-4 w-4" />
                                        Supported Networks
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {tokenInfo.networks.map((network) => (
                                        <span
                                            key={network}
                                            className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium"
                                        >
                                            {network}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right column: Token Utility + How to Participate */}
                    <div className="space-y-8">
                        {/* Token Utility Card */}
                        <div className="glass-card p-8">
                            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                                <Layers className="h-5 w-5 text-primary" />
                                Token Utility
                            </h3>

                            <div className="space-y-4">
                                {utilities.map((utility) => {
                                    const Icon = utility.icon
                                    return (
                                        <div
                                            key={utility.title}
                                            className="flex gap-4 p-4 rounded-xl bg-muted/30 group hover:bg-muted/50 transition-all"
                                        >
                                            <div className="h-12 w-12 shrink-0 flex items-center justify-center rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-all">
                                                <Icon className="h-6 w-6 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold mb-1">{utility.title}</h4>
                                                <p className="text-sm text-muted-foreground">{utility.description}</p>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        {/* How to Participate Card */}
                        <div className="glass-card p-8">
                            <h3 className="text-xl font-bold mb-8 flex items-center gap-2">
                                <Users className="h-5 w-5 text-primary" />
                                How to Participate
                            </h3>

                            <div className="grid gap-6 sm:grid-cols-2 mb-8">
                                {participateSteps.map((step, index) => {
                                    const Icon = step.icon
                                    return (
                                        <div key={step.title} className="flex gap-4 group">
                                            <div className="relative">
                                                <div className="h-12 w-12 flex items-center justify-center rounded-full bg-primary/10 group-hover:bg-primary/20 transition-all group-hover:scale-105">
                                                    <Icon className="h-6 w-6 text-primary" />
                                                </div>
                                            </div>
                                            <div>
                                                <h4 className="font-semibold mb-1">{step.title}</h4>
                                                <p className="text-sm text-muted-foreground">{step.description}</p>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>

                            {/* Referral Bonus */}
                            <div className="p-6 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 mb-8">
                                <div className="flex items-center gap-3 mb-2">
                                    <Users className="h-5 w-5 text-primary" />
                                    <span className="font-bold">Referral Program</span>
                                </div>
                                <p className="text-sm text-muted-foreground mb-3">
                                    Share your wallet address as a referral link and earn <strong className="text-primary">5% bonus TKS</strong> for
                                    every contribution made through your link!
                                </p>
                                <div className="flex items-center gap-2 text-sm text-primary font-medium">
                                    <CheckCircle2 className="h-4 w-4" />
                                    No limits on referral earnings
                                </div>
                            </div>

                            {/* CTA */}
                            <div className="flex flex-col gap-4 max-w-md mx-auto">
                                <Link href="/#hero" className="w-full">
                                    <Button size="lg" className="w-full btn-pill h-14 text-lg font-semibold gap-2">
                                        Buy TKS Now
                                        <ArrowRight className="h-5 w-5" />
                                    </Button>
                                </Link>
                                <p className="text-center text-sm text-muted-foreground">
                                    Scroll to the top to use the presale widget
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Current Pricing + Accepted Currencies — full width, side by side */}
                <div className="grid gap-8 lg:grid-cols-2 mt-8">
                    {/* Current Pricing Card */}
                    <div className="glass-card p-8">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold flex items-center gap-2">
                                <Coins className="h-5 w-5 text-primary" />
                                Current Pricing
                            </h3>
                            <div className="px-3 py-1 rounded-full bg-[#7ed957]/10 text-[#7ed957] text-sm font-medium">
                                Active
                            </div>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="p-6 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-center">
                                <div className="text-4xl font-bold text-primary mb-1">{presaleInfo.currentPrice}</div>
                                <div className="text-sm text-muted-foreground">per TKS</div>
                            </div>
                            <div className="p-6 rounded-xl bg-muted/30 text-center">
                                <div className="text-2xl font-bold mb-1">{presaleInfo.rate}</div>
                                <div className="text-sm text-muted-foreground">Exchange Rate</div>
                            </div>
                        </div>
                    </div>

                    {/* Accepted Currencies Card */}
                    <div className="glass-card p-8">
                        <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                            <CreditCard className="h-5 w-5 text-primary" />
                            Accepted Currencies
                        </h3>
                        <div className="flex flex-wrap gap-3">
                            {acceptedCurrencies.map((currency) => (
                                <div
                                    key={currency.name}
                                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/30 border border-border/50"
                                >
                                    <span className="font-semibold">{currency.name}</span>
                                    <span className="text-xs text-muted-foreground">({currency.type})</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Smart Contracts Card — full width */}
                <div className="glass-card p-8 mt-8">
                    <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                        <FileCode className="h-5 w-5 text-primary" />
                        Smart Contracts
                    </h3>

                    <div className="grid gap-8 lg:grid-cols-2">
                        {/* Mainnet */}
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <span className="px-2.5 py-0.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 text-xs font-semibold uppercase tracking-wider">
                                    Mainnet
                                </span>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground mb-2">TKS Token</p>
                                    <div className="space-y-1.5">
                                        {contracts.mainnet.token.map((c) => (
                                            <AddressRow key={`mt-${c.network}`} {...c} />
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground mb-2">Claim Vault <span className="text-xs opacity-60">(UUPS Proxy)</span></p>
                                    <div className="space-y-1.5">
                                        {contracts.mainnet.vault.map((c) => (
                                            <AddressRow key={`mv-${c.network}`} {...c} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Testnet */}
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <span className="px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-semibold uppercase tracking-wider">
                                    Testnet
                                </span>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground mb-2">TKS Token</p>
                                    <div className="space-y-1.5">
                                        {contracts.testnet.token.map((c) => (
                                            <AddressRow key={`tt-${c.network}`} {...c} />
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground mb-2">Claim Vault <span className="text-xs opacity-60">(UUPS Proxy)</span></p>
                                    <div className="space-y-1.5">
                                        {contracts.testnet.vault.map((c) => (
                                            <AddressRow key={`tv-${c.network}`} {...c} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
